/* global fetch */

import { config } from '../config';
import { headers, APIResponse, SpanTag, stleaf, fetchAPI } from '../helpers';

/**
 * Self-serve DJI FlightHub integration for the caller's organisation
 * (org-admin only). The API scopes everything to the authenticated user's
 * org — no ids are passed from the client.
 */

// Built inside each call (not hoisted to a module-level const) so it reads
// config.API_URL *after* config.setOpts() has run — a module-level snapshot
// would capture the empty default at import time.
const base = (): string => `${config.API_URL}/v2/organisation/dji-integration`;

export const get = (st: SpanTag): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'djog1e'), base(), {
    method: 'GET',
    credentials: 'include',
    headers,
  });

export const create = (
  st: SpanTag,
  params: { name?: string } = {},
): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'djog2c'), base(), {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });

/** Retry auto-provisioning of the intake bucket + credentials. */
export const provision = (st: SpanTag): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'djog3p'), `${base()}/provision`, {
    method: 'POST',
    credentials: 'include',
    headers,
  });

export const rotateToken = (st: SpanTag): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'djog4r'), `${base()}/rotate-token`, {
    method: 'POST',
    credentials: 'include',
    headers,
  });

export const update = (
  st: SpanTag,
  fields: { webhookSecret?: string | null; enabled?: boolean; name?: string },
): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'djog5u'), base(), {
    method: 'PATCH',
    credentials: 'include',
    headers,
    body: JSON.stringify(fields),
  });

/** Cycle the intake access key (distinct from rotateToken/webhook). Async —
 * re-fetch with get() to read the new AK/SK from the storage config. */
export const rotateCredentials = (st: SpanTag): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'djog6c'), `${base()}/rotate-credentials`, {
    method: 'POST',
    credentials: 'include',
    headers,
  });

/** Disconnect: tear down the intake infra and remove the integration. */
export const deprovision = (st: SpanTag): Promise<APIResponse> =>
  fetchAPI(stleaf(st, 'djog7d'), base(), {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
